import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface FiatConfig {
  id: string;
  currency: string;
  enabled: boolean;
  bank_enabled: boolean;
  bank_name: string;
  iban: string;
  swift_bic: string;
  account_holder: string;
  bank_message: string;
  card_enabled: boolean;
  card_message: string;
  paypal_enabled: boolean;
  paypal_message: string;
  min_deposit: number;
  max_deposit: number;
  min_withdrawal: number;
  max_withdrawal: number;
  updated_at: string;
}

export function useFiatConfig(enabledOnly = true) {
  return useQuery({
    queryKey: ["fiat-config", enabledOnly],
    queryFn: async () => {
      let query = supabase.from("fiat_payment_config").select("*").order("currency");
      if (enabledOnly) query = query.eq("enabled", true);
      const { data, error } = await query;
      if (error) throw error;
      return (data || []) as FiatConfig[];
    },
    staleTime: 60_000,
  });
}

export interface FiatOption {
  code: string;
  name: string;
  symbol: string;
  config?: FiatConfig;
}

/**
 * Merged fiat list for deposit/withdraw screens: every active currency from the
 * global `fiat_currencies` registry, joined with its payment configuration when
 * one exists. Currencies configured in `fiat_payment_config` but missing from
 * the registry are still included so nothing silently disappears.
 * The platform base currency is sorted first.
 */
export function useFiatOptions(baseCurrency = "USD") {
  const { data: configs = [], isLoading: configLoading } = useFiatConfig(true);

  const { data: registry = [], isLoading: registryLoading } = useQuery({
    queryKey: ["fiat-currencies-active"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("fiat_currencies")
        .select("currency_code, currency_name, iso_code")
        .eq("is_active", true)
        .order("sort_order", { ascending: true });
      if (error) throw error;
      return (data || []) as { currency_code: string; currency_name: string; iso_code: string }[];
    },
    staleTime: 300_000,
  });

  const byCode = new Map<string, FiatOption>();
  registry.forEach((r) => {
    byCode.set(r.currency_code, {
      code: r.currency_code,
      name: r.currency_name || r.currency_code,
      symbol: r.iso_code || r.currency_code,
      config: configs.find((c) => c.currency === r.currency_code),
    });
  });
  configs.forEach((c) => {
    if (!byCode.has(c.currency)) {
      byCode.set(c.currency, { code: c.currency, name: c.currency, symbol: c.currency, config: c });
    }
  });

  const options = Array.from(byCode.values()).sort((a, b) => {
    if (a.code === baseCurrency) return -1;
    if (b.code === baseCurrency) return 1;
    return a.code.localeCompare(b.code);
  });

  return { options, configs, isLoading: configLoading || registryLoading };
}
